import React, { useState, useEffect } from 'react';
import AppDownloadModal from '../components/gti/app-download-modal';
import { getMobileOperatingSystem } from './hooks';
import { getCurrentAdjustAppKind, trackThenNavigate } from './adjustSdk';
import { appDownloadDefaultLink } from './app-download-default-link';
import { appendClickIdsToLink, getCampaignAttribution } from './campaign-attribution';
import {
  downloadCtaPlacement,
  downloadModalQrTrackerLink,
  tagAdjustLinkWithCurrentPage
} from './adjust-page-tag';
import { CTA_DESTINATION, trackRegisterCtaClick } from './register-cta-tracking';

export default function useAppDownloadModal() {
  const [isOpenDownloadModal, setIsOpenDownloadModal] = useState(false);
  const [mobileOS, setMobileOS] = useState(null);
  const [downloadLink, setDownloadLink] = useState(null);
  const [qrLink, setQrLink] = useState(null);
  // SEOENG-125: the modal renders for the page's app (GTI or GTG): badges, QR, ratings and copy.
  const [modalAppKind, setModalAppKind] = useState('gti');

  useEffect(() => {
    const os = getMobileOperatingSystem();
    setMobileOS(os);
  }, []);

  const openDownloadModal = async ({ downloadLink = null, ctaLocation = null, placement = null } = {}) => {
    // DAR-235 follow-up: the button's placement (header | hero | sticky) rides every link this CTA
    // sends out as the Adjust creative, the QR included; derived from ctaLocation when not given,
    // and body for every other download CTA (SEOENG-167).
    const buttonPlacement = downloadCtaPlacement(placement, ctaLocation);
    // A CTA tapped before its paid link was generated (or one that never asked for a link) still
    // carries the Google click ids on the default link, so Adjust can hand the conversion back to
    // Google Ads even when the campaign structure is missing (SEOENG-38).
    const attribution = getCampaignAttribution();
    const withClickIds = (link) => (attribution ? appendClickIdsToLink(link, attribution) : link);
    const appKind = getCurrentAdjustAppKind();
    // SEOENG-117: a CTA with no link of its own (cara-beli-saham, the ticker buy card, a download
    // section still resolving) used to send the untagged default link; it now carries the page as
    // the Adjust adgroup like the generated organic link does.
    const finalLink = withClickIds(
      tagAdjustLinkWithCurrentPage(downloadLink || appDownloadDefaultLink(appKind), buttonPlacement)
    );

    // On a paid visit the modal always shows the click-id-bearing link (badges + a live QR), even
    // when the CTA was tapped before its deep link resolved. On an organic visit with no link the
    // modal keeps its store badges (tagged at click), and its QR becomes a live QR of the same
    // QR tracker with the page tag instead of the static image, which could not carry the page.
    setDownloadLink(
      attribution ? finalLink : downloadLink && tagAdjustLinkWithCurrentPage(downloadLink, buttonPlacement)
    );
    setQrLink(tagAdjustLinkWithCurrentPage(downloadModalQrTrackerLink(appKind), buttonPlacement));
    setModalAppKind(appKind === 'gti' ? 'gti' : 'gtg');

    const isMobileStoreRedirect = mobileOS === 'Android' || mobileOS === 'iOS';

    // SEOENG-56: the CTA click is the registration signal GA4 measures per page. Pushed before
    // the navigation/modal so a same-tab store redirect cannot unload the page first.
    trackRegisterCtaClick({
      ctaLocation,
      destination: isMobileStoreRedirect
        ? CTA_DESTINATION.STORE_REDIRECT
        : CTA_DESTINATION.DOWNLOAD_MODAL,
      link: finalLink
    });

    if (isMobileStoreRedirect) {
      await trackThenNavigate({
        url: finalLink,
        appKind,
        openInNewTab: true
      });
    } else {
      setIsOpenDownloadModal(true);
    }
  };

  const closeDownloadModal = () => setIsOpenDownloadModal(false);

  const downloadModal = (
    <AppDownloadModal
      isOpen={isOpenDownloadModal}
      onClose={closeDownloadModal}
      downloadLink={downloadLink}
      qrLink={qrLink}
      appKind={modalAppKind}
    />
  );

  return {
    openDownloadModal,
    downloadModal
  };
}
